import { Task, TaskApiStatus, WorkStatus } from '../types/task.type';

export type TaskSortField = 'due' | 'updated' | 'title' | 'employee' | 'status' | 'space' | 'task_list';
export type TaskSortDir = 'asc' | 'desc';
export type DueDateFilter = '' | 'overdue' | 'thisWeek' | 'noDue';
export type TaskStatusTab = 'all' | WorkStatus;
export type ObjectiveLinkFilter = '' | 'linked' | 'unlinked';

export const WORK_STATUSES: WorkStatus[] = [
  'not_started',
  'in_progress',
  'blocked',
  'on_hold',
  'completed',
];

export interface ClientTaskFilters {
  search?: string;
  employee_id?: string;
  space_name?: string;
  task_list_id?: string;
  dueDateFilter?: DueDateFilter;
  statusTab?: TaskStatusTab;
  objectiveLink?: ObjectiveLinkFilter;
  objectiveId?: string;
  teamEmployeeIds?: Set<string> | null;
}

export const WORK_STATUS_COLORS: Record<WorkStatus, { bg: string; color: string }> = {
  not_started: { bg: '#F3F4F6', color: '#6B7280' },
  in_progress: { bg: '#EFF6FF', color: '#2563EB' },
  blocked:    { bg: '#FEF2F2', color: '#DC2626' },
  on_hold:     { bg: '#FFFBEB', color: '#D97706' },
  completed:   { bg: '#F0FDF4', color: '#16A34A' },
};

const STATUS_SORT_ORDER: Record<WorkStatus, number> = {
  blocked: 0,
  on_hold: 1,
  in_progress: 2,
  not_started: 3,
  completed: 4,
};

const STATUS_LABEL_KEY: Record<WorkStatus, string> = {
  not_started: 'tasks.statuses.notStarted',
  in_progress: 'tasks.statuses.inProgress',
  blocked: 'tasks.statuses.blocked',
  on_hold: 'tasks.statuses.onHold',
  completed: 'tasks.statuses.completed',
};

export function taskKey(task: Pick<Task, 'employee_id' | 'task_list_id' | 'google_task_id'>): string {
  return `${task.employee_id}:${task.task_list_id}:${task.google_task_id}`;
}

export function sameTask(a: Pick<Task, 'employee_id' | 'task_list_id' | 'google_task_id'>, b: Pick<Task, 'employee_id' | 'task_list_id' | 'google_task_id'>): boolean {
  return a.google_task_id === b.google_task_id
    && a.task_list_id === b.task_list_id
    && a.employee_id === b.employee_id;
}

export function normalizeTask(raw: Task): Task {
  const status: TaskApiStatus = raw.status === 'completed' ? 'completed' : 'needsAction';
  const workStatus = WORK_STATUSES.includes(raw.work_status)
    ? raw.work_status
    : (status === 'completed' ? 'completed' : 'not_started');
  return {
    ...raw,
    status,
    work_status: workStatus,
    linked_objectives: Array.isArray(raw.linked_objectives) ? raw.linked_objectives : [],
  };
}

export function isOverdue(task: Pick<Task, 'is_overdue' | 'work_status'>): boolean {
  if (task.work_status === 'completed') return false;
  return task.is_overdue;
}

export function getWorkStatus(task: Pick<Task, 'work_status' | 'status'>): WorkStatus {
  if (task.work_status && WORK_STATUSES.includes(task.work_status)) return task.work_status;
  return task.status === 'completed' ? 'completed' : 'not_started';
}

export function isTaskCompleted(task: Pick<Task, 'work_status' | 'status'>): boolean {
  return getWorkStatus(task) === 'completed';
}

export function workStatusLabelKey(status: WorkStatus): string {
  return STATUS_LABEL_KEY[status];
}

export function buildWorkStatusWrite(task: Pick<Task, 'work_status' | 'status'>, next: WorkStatus): { work_status?: WorkStatus; status?: TaskApiStatus } {
  if (next === 'completed') return { work_status: 'completed' };
  if (isTaskCompleted(task)) return { status: 'needsAction', work_status: next };
  return { work_status: next };
}

export function buildToggleCompleteWrite(task: Pick<Task, 'work_status' | 'status'>): { work_status?: WorkStatus; status?: TaskApiStatus } {
  if (isTaskCompleted(task)) return { status: 'needsAction' };
  return { work_status: 'completed' };
}

export function dueHintParts(task: Pick<Task, 'due' | 'work_status' | 'status'>): { key: string; count?: number } | null {
  const dayOffset = dueDayOffset(task.due);
  if (dayOffset === null) return null;
  if (isTaskCompleted(task)) {
    if (dayOffset < 0) return { key: 'tasks.daysSinceCompletion', count: Math.abs(dayOffset) };
    return null;
  }
  if (dayOffset === 0) return { key: 'tasks.dueToday' };
  if (dayOffset > 0) return { key: 'tasks.daysLeft', count: dayOffset };
  return { key: 'tasks.daysOverdue', count: Math.abs(dayOffset) };
}

export function dueDayOffset(due: string | null | undefined): number | null {
  if (!due) return null;
  const [year, month, day] = due.slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) return null;
  const dueUtc = Date.UTC(year, month - 1, day);
  const now = new Date();
  const todayUtc = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((dueUtc - todayUtc) / 86400000);
}

function isDueThisWeek(due: string): boolean {
  const d = new Date(due);
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const end = new Date(start);
  end.setDate(end.getDate() + 7);
  return d >= start && d < end;
}

export function getStatusCounts(tasks: Task[]) {
  const counts = {
    all: tasks.length,
    not_started: 0,
    in_progress: 0,
    blocked: 0,
    on_hold: 0,
    completed: 0,
  };
  for (const task of tasks) {
    counts[getWorkStatus(task)] += 1;
  }
  return counts;
}

export function getUniqueSpaces(tasks: Task[]): string[] {
  const names = new Set<string>();
  tasks.forEach(t => {
    if (t.space_name) names.add(t.space_name);
  });
  return Array.from(names).sort((a, b) => a.localeCompare(b));
}

export function getUniqueTaskLists(tasks: Task[]): { id: string; name: string }[] {
  const map = new Map<string, string>();
  tasks.forEach(t => {
    if (t.task_list_id) map.set(t.task_list_id, t.task_list_name);
  });
  return Array.from(map.entries())
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function getLinkedObjectives(tasks: Task[]): { id: string; title: string }[] {
  const map = new Map<string, string>();
  for (const task of tasks) {
    for (const objective of task.linked_objectives ?? []) {
      if (objective?.id) map.set(objective.id, objective.title);
    }
  }
  return Array.from(map.entries())
    .map(([id, title]) => ({ id, title }))
    .sort((a, b) => a.title.localeCompare(b.title));
}

export function filterTasks(tasks: Task[], filters: ClientTaskFilters): Task[] {
  return tasks.filter(task => {
    if (filters.statusTab && filters.statusTab !== 'all' && getWorkStatus(task) !== filters.statusTab) {
      return false;
    }

    if (filters.objectiveLink === 'linked' && (task.linked_objectives?.length ?? 0) === 0) return false;
    if (filters.objectiveLink === 'unlinked' && (task.linked_objectives?.length ?? 0) > 0) return false;
    if (filters.objectiveId && !(task.linked_objectives ?? []).some(o => o.id === filters.objectiveId)) return false;

    if (filters.search) {
      const q = filters.search.toLowerCase();
      const linkedTitle = (task.linked_objectives ?? []).some(o => o.title.toLowerCase().includes(q));
      if (
        !task.title.toLowerCase().includes(q) &&
        !task.employee_name.toLowerCase().includes(q) &&
        !(task.space_name?.toLowerCase().includes(q)) &&
        !task.task_list_name.toLowerCase().includes(q) &&
        !linkedTitle
      ) return false;
    }

    if (filters.employee_id && task.employee_id !== filters.employee_id) return false;
    if (filters.space_name && task.space_name !== filters.space_name) return false;
    if (filters.task_list_id && task.task_list_id !== filters.task_list_id) return false;

    if (filters.teamEmployeeIds !== null && filters.teamEmployeeIds !== undefined) {
      if (!filters.teamEmployeeIds.has(task.employee_id)) return false;
    }

    const dueFilter = filters.dueDateFilter;
    if (dueFilter === 'overdue' && !isOverdue(task)) return false;
    if (dueFilter === 'thisWeek' && (!task.due || !isDueThisWeek(task.due))) return false;
    if (dueFilter === 'noDue' && task.due) return false;

    return true;
  });
}

function compareDue(a: Task, b: Task, dir: TaskSortDir): number {
  const mul = dir === 'asc' ? 1 : -1;
  const aOver = isOverdue(a);
  const bOver = isOverdue(b);
  if (aOver !== bOver) return aOver ? -1 : 1;

  const aDue = a.due ? new Date(a.due).getTime() : (dir === 'asc' ? Infinity : -Infinity);
  const bDue = b.due ? new Date(b.due).getTime() : (dir === 'asc' ? Infinity : -Infinity);
  return (aDue - bDue) * mul;
}

function compareStrings(a: string, b: string, dir: TaskSortDir): number {
  return a.localeCompare(b) * (dir === 'asc' ? 1 : -1);
}

export function sortTasks(tasks: Task[], field: TaskSortField, dir: TaskSortDir): Task[] {
  return [...tasks].sort((a, b) => {
    switch (field) {
      case 'due':
        return compareDue(a, b, dir);
      case 'updated':
        return (new Date(a.updated).getTime() - new Date(b.updated).getTime()) * (dir === 'asc' ? 1 : -1);
      case 'title':
        return compareStrings(a.title, b.title, dir);
      case 'employee':
        return compareStrings(a.employee_name, b.employee_name, dir);
      case 'space':
        return compareStrings(a.space_name ?? '', b.space_name ?? '', dir);
      case 'task_list':
        return compareStrings(a.task_list_name, b.task_list_name, dir);
      case 'status': {
        const orderA = STATUS_SORT_ORDER[getWorkStatus(a)];
        const orderB = STATUS_SORT_ORDER[getWorkStatus(b)];
        return (orderA - orderB) * (dir === 'asc' ? 1 : -1);
      }
      default:
        return 0;
    }
  });
}

export function toggleSortDir(currentField: TaskSortField, currentDir: TaskSortDir, newField: TaskSortField): TaskSortDir {
  if (currentField === newField) return currentDir === 'asc' ? 'desc' : 'asc';
  if (newField === 'updated') return 'desc';
  if (newField === 'due') return 'asc';
  return 'asc';
}

export function getSortLabel(
  sortField: TaskSortField,
  sortDir: TaskSortDir,
  t: (key: string) => string,
): string {
  if (sortField === 'due') {
    return sortDir === 'asc' ? t('tasks.sortByNearest') : t('tasks.sortByFarthest');
  }
  if (sortField === 'updated') return t('tasks.sortByUpdated');
  const fieldLabels: Partial<Record<TaskSortField, string>> = {
    title: t('tasks.tableTask'),
    employee: t('tasks.tableAssignee'),
    space: t('tasks.tableSpace'),
    status: t('tasks.tableStatus'),
    task_list: t('tasks.googleList'),
  };
  const label = fieldLabels[sortField] ?? '';
  return sortDir === 'asc' ? `${label} ↑` : `${label} ↓`;
}
